import React, { useState, useEffect } from 'react';
import { Form, Select, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getBlocks } from '@/api/properties';
import { useAuth } from '@/contexts/AuthContext';
import type { Block, Floor } from '@/types/resources/property';

interface BlockFloorSelectorProps {
  disabled?: boolean;
  onBlockDataChange?: (block: { id: string; name: string } | null) => void;
  onFloorDataChange?: (floor: { name: string; slug: string } | null) => void;
}

export const BlockFloorSelector: React.FC<BlockFloorSelectorProps> = ({
  disabled = false,
  onBlockDataChange,
  onFloorDataChange
}) => {
  const { t } = useTranslation(['properties', 'common']);
  const { firestoreUser } = useAuth();
  const form = Form.useFormInstance();
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  // Fetch blocks
  const { data: blocksData } = useQuery({
    queryKey: ['blocks', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getBlocks({
        searchTerm: '',
        pageSize: 1000,
        firestoreUser
      });
    },
    enabled: !!firestoreUser?.workspace?.id
  });

  const selectedBlock = blocksData?.blocks?.find((block: Block) => block.id === selectedBlockId);
  const availableFloors = selectedBlock?.floors || [];

  // Initialize selectedBlockId from form values when component mounts or form values change
  useEffect(() => {
    const blockId = form.getFieldValue('blockId');
    if (blockId && blockId !== selectedBlockId) {
      setSelectedBlockId(blockId);
      
      // Provide block data to parent
      const block = blocksData?.blocks?.find((b: Block) => b.id === blockId);
      onBlockDataChange?.(block ? { id: block.id, name: block.name } : null);
      
      // Provide floor data to parent if floor is already selected
      const floorSlug = form.getFieldValue('floorSlug');
      if (floorSlug) {
        const floor = block?.floors?.find((f: Floor) => f.slug === floorSlug);
        onFloorDataChange?.(floor ? { name: floor.name, slug: floor.slug } : null);
      }
    }
  }, [form, blocksData, selectedBlockId, onBlockDataChange, onFloorDataChange]);

  // Additional effect to handle form value changes after initial load
  useEffect(() => {
    const blockId = form.getFieldValue('blockId');
    if (blockId && !selectedBlockId) {
      setSelectedBlockId(blockId);
    }
  }, [form, selectedBlockId]);

  const handleBlockChange = (blockId: string) => {
    setSelectedBlockId(blockId);
    
    // Provide block data to parent
    if (blockId) {
      const block = blocksData?.blocks?.find((b: Block) => b.id === blockId);
      onBlockDataChange?.(block ? { id: block.id, name: block.name } : null);
    } else {
      onBlockDataChange?.(null);
    }
    
    // Reset floor data when block changes
    onFloorDataChange?.(null);
  };

  const handleFloorChange = (floorSlug: string) => {
    // Provide floor data to parent
    if (floorSlug) {
      const floor = availableFloors.find((f: Floor) => f.slug === floorSlug);
      onFloorDataChange?.(floor ? { name: floor.name, slug: floor.slug } : null);
    } else {
      onFloorDataChange?.(null);
    }
  };

  return (
    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          name="blockId"
          label={t('properties:common.blockId')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <Select
            placeholder={t('properties:blocks.details.attributes.selectBlock')}
            onChange={handleBlockChange}
            disabled={disabled}
            allowClear
          >
            {blocksData?.blocks?.map((block: Block) => (
              <Select.Option key={block.id} value={block.id}>
                {block.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="floorSlug"
          label={t('properties:common.floorId')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <Select
            placeholder={t('properties:floors.details.attributes.selectFloor')}
            onChange={handleFloorChange}
            disabled={disabled || !selectedBlockId}
            allowClear
          >
            {availableFloors.map((floor: Floor) => (
              <Select.Option key={floor.slug} value={floor.slug}>
                {floor.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    </Row>
  );
}; 